// Netherlands requires a sole proprietorship for individuals.
// According to Stripe: "we’re enforcing stricter business type requirements for Netherlands (NL)
// accounts to ensure compliance with Dutch regulations. This specifically affects
// how we collect the KvK (Kamer van Koophandel), the unique 8-digit company registration number
// required for businesses in the Netherlands."
// https://docs.stripe.com/connect/upcoming-requirements-updates?program=eu-2025#netherlands-business-registration-requirements
const countriesRequiringSoleProprietorshipForIndividuals = new Set(['NL']);

/**
 * Whether Connect onboarding for an individual in this country must use a company account with
 * sole proprietorship structure (Stripe compliance; currently applies to NL).
 *
 * @param {string} country - ISO 3166-1 alpha-2 country code
 * @returns {boolean}
 */
export const requiresSoleProprietorshipAccount = country =>
  countriesRequiringSoleProprietorshipForIndividuals.has(country);

/**
 * Builds `business_type` and optional `company.structure` for Stripe account creation or token
 * flows when the seller's country and account type trigger sole-proprietorship rules.
 *
 * @param {Object} params
 * @param {string} params.country - ISO 3166-1 alpha-2 country code
 * @param {string} params.accountType - Stripe business type, e.g. `individual` or `company`
 * @returns {Object} Either `{ business_type: 'company', company: { structure: 'sole_proprietorship' } }`
 *   for individuals in restricted countries, or `{ business_type: accountType }` otherwise
 */
export const getStripeAccountTokenInfo = ({ country, accountType }) => {
  const isIndividualInRestrictedCountry =
    accountType === 'individual' && requiresSoleProprietorshipAccount(country);

  return isIndividualInRestrictedCountry
    ? {
        business_type: 'company',
        company: {
          structure: 'sole_proprietorship',
        },
      }
    : {
        business_type: accountType,
      };
};

/////////////////////////////////////////////////////////////////////////
// Payout readiness                                                    //
//                                                                     //
// A vendor with no working Stripe Connect account still sees their    //
// listings sitting there looking published, but nobody can buy them:  //
// the order form refuses to take money that has nowhere to go. That   //
// failure is completely silent from the vendor's side, so these       //
// helpers give the rest of the app one shared answer to "can this     //
// person actually be paid?".                                          //
/////////////////////////////////////////////////////////////////////////

/** Payouts are set up and Stripe isn't asking for anything. */
export const PAYOUT_DETAILS_OK = 'ok';
/** No Stripe Connect account at all - onboarding was never finished. */
export const PAYOUT_DETAILS_MISSING = 'missing';
/** An account exists, but Stripe wants more information before releasing money. */
export const PAYOUT_DETAILS_INCOMPLETE = 'incomplete';
/** The Connect account hasn't been fetched yet, so we don't know. */
export const PAYOUT_DETAILS_UNKNOWN = 'unknown';

// Stripe groups outstanding verification requirements by urgency. 'currently_due' is
// "we need this soon", 'past_due' is "payouts are already affected". Both mean the
// vendor has something to do, which is all this needs to distinguish.
const REQUIREMENT_TYPES = ['past_due', 'currently_due'];

const hasRequirements = (stripeAccountData, requirementType) =>
  Array.isArray(stripeAccountData?.requirements?.[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

/**
 * Is Stripe waiting on information from this vendor?
 *
 * @param {Object} [stripeAccountData] - `stripeAccount.attributes.stripeAccountData`
 * @returns {boolean}
 */
export const hasStripeRequirementsDue = stripeAccountData =>
  REQUIREMENT_TYPES.some(type => hasRequirements(stripeAccountData, type));

/**
 * Where a vendor stands on getting paid.
 *
 * @param {Object} params
 * @param {Object} [params.currentUser] - the current user entity
 * @param {Object} [params.stripeAccount] - state.stripeConnectAccount.stripeAccount
 * @param {boolean} [params.stripeAccountFetched] - whether the account has been loaded
 * @returns {'ok'|'missing'|'incomplete'|'unknown'}
 */
export const getPayoutDetailsStatus = params => {
  const { currentUser, stripeAccount, stripeAccountFetched } = params || {};

  if (!currentUser?.id) {
    return PAYOUT_DETAILS_UNKNOWN;
  }
  // The current user is fetched with `include: ['stripeAccount']`, so the absence of
  // an account is knowable without loading the account itself.
  if (!currentUser.stripeAccount?.id) {
    return PAYOUT_DETAILS_MISSING;
  }
  // There is an account, but its details (and therefore its requirements) arrive in a
  // separate request. Until that lands, say nothing rather than guess.
  if (!stripeAccountFetched || !stripeAccount) {
    return PAYOUT_DETAILS_UNKNOWN;
  }

  return hasStripeRequirementsDue(stripeAccount.attributes?.stripeAccountData)
    ? PAYOUT_DETAILS_INCOMPLETE
    : PAYOUT_DETAILS_OK;
};

/**
 * Normalizes Stripe account data for display: company accounts with sole proprietorship structure
 * are shown as `individual` to match how the seller signed up.
 *
 * @param {Object} [stripeAccountData] - Account object with optional `business_type` and `company.structure`
 * @returns {string|undefined} Display type (`individual` for mapped sole props, otherwise `business_type`, or
 *   `undefined` when missing)
 */
export const getDisplayAccountType = stripeAccountData => {
  const businessType = stripeAccountData?.business_type;
  const companyStructure = stripeAccountData?.company?.structure;

  return businessType === 'company' && companyStructure === 'sole_proprietorship'
    ? 'individual'
    : businessType;
};
